// The fleet (P-004), owners only: the machines that report to the board and what their agents and
// runners are doing now (Machines, milestone 1), and CI runs, jobs, steps and trends (CI,
// milestone 2, components/Ci.tsx). Polls the API every 5 s while visible, every second while a
// restart is on its way.
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, type Fleet } from '../api/client';
import { errorMessage } from '../api/client';
import { CiTab } from '../components/Ci';
import { HostCard, UnattachedRunners } from '../components/Fleet';
import { Shell } from '../components/Shell';
import { Badge, Callout, Card, EmptyState, PageHeader, Stat, TabPanel, Tabs, buttonClass } from '../components/ui';
import { Icon } from '../components/Icon';
import { settingsSectionPath } from '../lib/routes';
import { findWorkspace, useSession } from '../lib/session';
import { ago } from '../lib/time';
import { useNow, usePoll } from '../lib/usePoll';
import { NotFoundPage } from './NotFoundPage';

const TABS = [
  { id: 'machines', label: 'Machines' },
  { id: 'ci', label: 'CI' },
];

function restarting(f: Fleet): boolean {
  return f.runners.some((r) => r.last_command && ['queued', 'picked'].includes(r.last_command.state))
    || f.hosts.some((h) => h.update_command && ['queued', 'picked'].includes(h.update_command.state))
    || (f.reconciliations ?? []).some((r) => ['sending', 'cleanup_pending'].includes(r.state));
}

function Summary({ fleet }: { fleet: Fleet }) {
  const s = fleet.summary;
  return (
    <div className="grid grid-cols-4 gap-3 narrow:grid-cols-2" aria-label="Summary" role="group">
      <Stat value={s.online} label={`machines of ${s.hosts} reporting`} tone="success" />
      <Stat value={s.coding_working} label="coding agents working" tone="signal" />
      <Stat
        value={s.slot_limit === null ? '–' : `${s.slots_used} of ${s.slot_limit}`}
        label={s.slot_limit === null ? 'slots: no machine reports a limit' : 'slots used'}
        tone={s.slot_limit !== null && s.slots_used > s.slot_limit ? 'warning' : 'neutral'}
      />
      <Stat value={s.runners_busy} label={`runners busy of ${s.runners}`} tone="signal" />
    </div>
  );
}

function MergeReconciliation({
  reconciliation,
  ws,
  reload,
}: {
  reconciliation: NonNullable<Fleet['reconciliations']>[number];
  ws: number;
  reload: () => void;
}) {
  const [itemId, setItemId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const linkable = reconciliation.state === 'no_item' && (reconciliation.linkable_items?.length ?? 0) > 0;
  async function link() {
    if (!itemId || busy) return;
    setBusy(true);
    setError('');
    const result = await api.POST('/api/workspaces/{ws_id}/fleet/reconciliations/{reconciliation_id}/link', {
      params: { path: { ws_id: ws, reconciliation_id: reconciliation.id } },
      body: { item_id: itemId },
    });
    setBusy(false);
    if (result.error) {
      setError(errorMessage(result.error));
      return;
    }
    reload();
  }
  return (
    <div className="grid gap-1 border-t border-hairline px-4 py-2.5 text-[12px]">
      <div className="flex flex-wrap items-center gap-2">
        <a href={reconciliation.pr_url} target="_blank" rel="noreferrer" className="font-medium text-ink-display">
          {reconciliation.item_id ?? reconciliation.repository} · PR #{reconciliation.pr_number}
        </a>
        <Badge tone={reconciliation.state === 'complete' ? 'success' : ['failed', 'rejected'].includes(reconciliation.state) ? 'danger' : ['no_item', 'release'].includes(reconciliation.state) ? 'muted' : 'warning'}>
          {reconciliation.state === 'release' ? `Release → ${reconciliation.default_branch}` : reconciliation.state === 'rejected' ? 'Rejected' : reconciliation.state === 'no_item' ? 'No planning item' : reconciliation.state === 'cleanup_pending' ? `cleanup pending${reconciliation.host ? ` · ${reconciliation.host}` : ''}` : reconciliation.state}
        </Badge>
      </div>
      {(reconciliation.last_error || reconciliation.cleanup_error) && (
        <p className="m-0 text-danger-ink" role="status">{reconciliation.last_error ?? reconciliation.cleanup_error}</p>
      )}
      {linkable && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <label className="sr-only" htmlFor={`merge-item-${reconciliation.id}`}>Planning item for PR #{reconciliation.pr_number}</label>
          <select
            id={`merge-item-${reconciliation.id}`}
            className="min-h-9 min-w-56 rounded-md border border-hairline bg-canvas px-2 text-ink-display"
            value={itemId}
            onChange={(event) => setItemId(event.target.value)}
            disabled={busy}
          >
            <option value="">Choose planning item</option>
            {reconciliation.linkable_items?.map((item) => (
              <option key={item.id} value={item.id}>{item.id} · {item.title}</option>
            ))}
          </select>
          <button type="button" className={buttonClass('secondary')} onClick={link} disabled={!itemId || busy}>
            {busy ? 'Linking…' : 'Link and retry'}
          </button>
          {error && <span className="text-danger-ink" role="alert">{error}</span>}
        </div>
      )}
    </div>
  );
}

function Machines({ fleet, ws, now, reload }: { fleet: Fleet; ws: number; now: number; reload: () => void }) {
  const over = fleet.hosts.filter((h) => h.online && h.slot_limit !== null && h.slots_used > h.slot_limit);
  const noHosts = fleet.hosts.length === 0;
  const reconciliations = fleet.reconciliations ?? [];
  if (noHosts && reconciliations.length === 0) {
    return (
      <EmptyState
        title="No machines report to this board yet"
        action={
          <Link to={settingsSectionPath(ws, 'hosts')} className={buttonClass('primary')}>
            <Icon name="plus" />
            Add a host
          </Link>
        }
      >
        Add a host in Settings, then run the install command it shows on that machine.
      </EmptyState>
    );
  }
  return (
    <div className="grid gap-3.5">
      {!noHosts && <Summary fleet={fleet} />}
      {reconciliations.length > 0 && (
        <Card as="section" padding="none" aria-label="Merge reconciliation">
          <h2 className="m-0 px-4 pb-1.5 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted">Merged pull requests</h2>
          {reconciliations.slice(0, 8).map((r) => (
            <MergeReconciliation key={r.id} reconciliation={r} ws={ws} reload={reload} />
          ))}
        </Card>
      )}
      {noHosts && (
        <EmptyState
          title="No machines report to this board yet"
          action={
            <Link to={settingsSectionPath(ws, 'hosts')} className={buttonClass('primary')}>
              <Icon name="plus" />
              Add a host
            </Link>
          }
        >
          Add a host in Settings, then run the install command it shows on that machine.
        </EmptyState>
      )}
      {over.map((h) => (
        <Callout key={h.id} tone="warning" role="status" icon={<Icon name="warn" />}>
          <b className="font-semibold">{h.name}</b> is running {h.slots_used} coding agents against a limit of {h.slot_limit}.
        </Callout>
      ))}
      {fleet.hosts.map((h) => (
        <HostCard key={h.id} host={h} ws={ws} now={now} onChanged={reload} />
      ))}
      <UnattachedRunners runners={fleet.runners.filter((runner) => runner.host === null)} ws={ws} now={now} />
    </div>
  );
}

function FleetBody({ ws }: { ws: number }) {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'ci' ? 'ci' : 'machines';
  const now = useNow(1000);
  // Every 5 s; every second while a restart is on its way, so Queued turns to Restarted at once.
  const [every, setEvery] = useState(5000);
  const poll = usePoll(
    () => api.GET('/api/workspaces/{ws_id}/fleet', { params: { path: { ws_id: ws } } }),
    `${ws}`,
    every,
  );
  const fast = poll.result.state === 'ok' && restarting(poll.result.data);
  useEffect(() => setEvery(fast ? 1000 : 5000), [fast]);
  return (
    <div className="mx-auto grid w-full max-w-[1120px] gap-4 px-5 pb-12 pt-6 phone:px-3 phone:pb-24 phone:pt-4">
      <PageHeader
        title="Fleet"
        purpose={
          tab === 'machines'
            ? 'The machines your agents and CI run on, and what each is doing now.'
            : 'Every run on the repos the GitHub App is installed on, newest first.'
        }
        actions={
          poll.fetchedAt !== null && (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-muted" aria-live="off">
              <span className={poll.failed ? 'size-2 rounded-full bg-warning' : 'size-2 rounded-full bg-success'} aria-hidden="true" />
              {poll.failed ? 'Could not refresh; showing data from ' : 'Updated '}
              {ago(new Date(poll.fetchedAt).toISOString(), now)}
            </span>
          )
        }
      />
      <Tabs
        tabs={TABS}
        value={tab}
        onValueChange={(id) => setParams(id === 'ci' ? { tab: 'ci' } : {}, { replace: true })}
        label="Fleet"
        variant="underline"
        idPrefix="fleet"
      />
      <TabPanel id={tab} idPrefix="fleet">
        {tab === 'ci' ? (
          <CiTab ws={ws} fleet={poll.result.state === 'ok' ? poll.result.data : null} now={now} />
        ) : poll.result.state === 'loading' ? (
          <p className="text-ink-muted" aria-busy="true">
            Loading…
          </p>
        ) : poll.result.state === 'error' ? (
          <Callout role="alert">{poll.result.message}</Callout>
        ) : (
          <Machines fleet={poll.result.data} ws={ws} now={now} reload={poll.reload} />
        )}
      </TabPanel>
    </div>
  );
}

export function FleetPage() {
  const ws = Number(useParams().ws);
  const { me } = useSession();
  const workspace = findWorkspace(me, ws);
  // Owners only: a viewer gets what a missing page gets.
  if (!workspace || workspace.role !== 'owner') return <NotFoundPage />;
  return (
    <Shell ws={ws}>
      <FleetBody ws={ws} />
    </Shell>
  );
}
